import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchStores } from "../store/actions/storeActions";
import { getCouponsByCategorySlug } from "../store/actions/couponActions";
import { getCategories } from "../store/actions/categoryActions";
import { findCategoryBySlug } from "../utils/categoryHelpers";
import CategoryDetailHero from "../components/category-detail/CategoryDetailHero";
import CategoryDetailContent from "../components/category-detail/CategoryDetailContent";
import useSEO from "../hooks/useSEO";
import seoConfig from "../config/seoConfig";
import { generateBreadcrumbSchema, generateFAQSchema } from "../utils/schemaGenerators";
import FaqAccordion from "../components/legal/FaqAccordion";
import styled from "styled-components";

const FaqWrap = styled.div`
  max-width: min(100%, 48rem);
  margin: 2rem auto 0;
  padding: 0 1rem;
`;

export default function CategoryDetailPage() {
  const dispatch = useDispatch();
  const { slug } = useParams();
  const categorySlug = slug || "";

  const categories = useSelector((s) => s.category.categories) || [];
  const reduxStores = useSelector((s) => s.store.stores) || [];
  const categoryCoupons = useSelector((s) => s.coupon.categoryCoupons) || [];
  const storeLoading = useSelector((s) => s.store.loading);
  const couponLoading = useSelector((s) => s.coupon.loading);
  const categoryLoading = useSelector((s) => s.category.loading);

  const category = findCategoryBySlug(categories, categorySlug);

  useEffect(() => {
    if (!categorySlug) return;
    dispatch(fetchStores());
    dispatch(getCategories());
    dispatch(getCouponsByCategorySlug(categorySlug));
  }, [categorySlug, dispatch]);

  const loading = storeLoading || couponLoading || categoryLoading;

  const categoryFaqs = (category?.faqs || []).filter((f) => f.question && f.answer);

  const seo = useSEO({
    title: category?.metaTitle || (category ? `${category.name} Coupons` : undefined),
    description: category?.metaDescription || category?.description || undefined,
    image: category?.image,
    schema: category
      ? [
          generateBreadcrumbSchema([
            { name: "Home", url: seoConfig.siteUrl },
            { name: "Categories", url: `${seoConfig.siteUrl}/categories` },
            {
              name: category.name,
              url: `${seoConfig.siteUrl}/category/${category.slug || categorySlug}`,
            },
          ]),
          categoryFaqs.length
            ? generateFAQSchema(
                categoryFaqs.map((f) => ({
                  question: f.question,
                  answer: f.answer,
                }))
              )
            : null,
        ].filter(Boolean)
      : undefined,
  });

  if (!loading && !category) {
    return (
      <>
        {seo}
        <CategoryDetailContent
        slug={categorySlug}
        category={null}
        categories={categories}
        stores={reduxStores}
        coupons={categoryCoupons}
        />
      </>
    );
  }

  return (
    <>
      {seo}
      {category && <CategoryDetailHero category={category} />}
      {loading && (
        <p style={{ textAlign: "center", padding: "2rem", color: "hsl(220 10% 46%)" }}>
          Loading category…
        </p>
      )}
      <CategoryDetailContent
        slug={categorySlug}
        category={category}
        categories={categories}
        stores={reduxStores}
        coupons={categoryCoupons}
      />
      {categoryFaqs.length > 0 && (
        <FaqWrap>
          <FaqAccordion
            items={categoryFaqs.map((f) => ({
              q: f.question,
              a: f.answer,
            }))}
          />
        </FaqWrap>
      )}
    </>
  );
}
